import React from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { Button, BadgeAlt as Badge, Checkbox, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, } from '@cargomatic/ui';
import { ChevronRight, ChevronsRight, ChevronsDown, GripVertical, MoreHorizontal, Pencil, X, Eye, Plus, FileText, Mail, Download, } from 'lucide-react';
import { getStatusBadge, getBillingStatusBadge, getLegTypeBadge, } from './utils';
import { Link } from 'react-router-dom';
const columnHelper = createColumnHelper();
export function createColumns(options) {
    const { selectedShipments, expandedRows, expandedEquipment, isMounted, allExpanded, shipments: _shipments, handleSelectShipment, toggleExpand, toggleEquipmentExpand, handleExpandAll, handleCollapseAll, onEditShipment, onDeleteShipment, onOpenAddHub, onEditEquipment, dragListenersMapRef, } = options;
    return [
        columnHelper.display({
            id: 'select',
            header: ({ table }) => {
                const currentPageRows = table.getRowModel().rows;
                const currentPageIds = currentPageRows.map((row) => row.original._id);
                const selectedOnPage = currentPageIds.filter((id) => selectedShipments.includes(id));
                const isAllSelected = currentPageIds.length > 0 &&
                    selectedOnPage.length === currentPageIds.length;
                const isIndeterminate = selectedOnPage.length > 0 &&
                    selectedOnPage.length < currentPageIds.length;
                return (React.createElement("div", { className: "flex items-center gap-2" },
                    React.createElement(TooltipProvider, null,
                        React.createElement(Tooltip, null,
                            React.createElement(TooltipTrigger, { asChild: true },
                                React.createElement(Button, { variant: "ghost", size: "sm", className: "h-6 w-6 p-0 hover:bg-muted", onClick: allExpanded ? handleCollapseAll : handleExpandAll }, allExpanded ? (React.createElement(ChevronsDown, { className: "h-3.5 w-3.5 text-muted-foreground" })) : (React.createElement(ChevronsRight, { className: "h-3.5 w-3.5 text-muted-foreground" })))),
                            React.createElement(TooltipContent, null, allExpanded ? 'Collapse all' : 'Expand all'))),
                    React.createElement(Checkbox, { checked: isAllSelected, ref: (el) => {
                            const checkbox = el;
                            if (checkbox)
                                checkbox.indeterminate = isIndeterminate;
                        }, onCheckedChange: (value) => {
                            currentPageRows.forEach((row) => {
                                handleSelectShipment(row.original._id, !!value);
                            });
                        } })));
            },
            cell: ({ row }) => {
                const isExpanded = expandedRows.has(row.original._id);
                const dragListeners = dragListenersMapRef.current.get(row.original._id) || {};
                return (React.createElement("div", { className: "flex items-center gap-2" },
                    React.createElement("button", { type: "button", className: "h-6 w-6 p-0 inline-flex items-center justify-center rounded hover:bg-muted", onClick: () => toggleExpand(row.original._id) },
                        React.createElement(ChevronRight, { className: `h-4 w-4 transition-transform ${isExpanded ? 'rotate-90' : ''}` })),
                    React.createElement("div", Object.assign({ className: "cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded" }, dragListeners),
                        React.createElement(GripVertical, { className: "h-4 w-4 text-muted-foreground" })),
                    React.createElement(Checkbox, { checked: selectedShipments.includes(row.original._id), onCheckedChange: (checked) => handleSelectShipment(row.original._id, !!checked) })));
            },
            size: 120,
        }),
        columnHelper.display({
            id: 'sequence',
            header: () => React.createElement("div", { className: "text-right" }, "Leg Number"),
            cell: ({ row, table }) => {
                const pageIndex = table.getState().pagination.pageIndex;
                const pageSize = table.getState().pagination.pageSize;
                const rowIndex = row.index;
                const sequenceNumber = pageIndex * pageSize + rowIndex + 1;
                return (React.createElement("div", { className: "text-right" },
                    React.createElement("span", { className: "font-medium text-muted-foreground" }, sequenceNumber)));
            },
            size: 60,
        }),
        columnHelper.accessor('shipment_id', {
            header: 'ID',
            cell: ({ row }) => (React.createElement("a", { href: `/shipments/${row.original._id}`, target: "_blank", rel: "noopener noreferrer", className: "text-blue-600 font-medium hover:underline whitespace-nowrap" }, row.original.shipment_id)),
            size: 100,
        }),
        columnHelper.accessor('status', {
            header: 'Status',
            cell: ({ getValue }) => (React.createElement("div", { className: "whitespace-nowrap" }, getStatusBadge(getValue()))),
            size: 120,
        }),
        columnHelper.accessor('billingStatus', {
            header: 'Billing Status',
            cell: ({ getValue }) => (React.createElement("div", { className: "whitespace-nowrap" }, getBillingStatusBadge(getValue()))),
            size: 200,
        }),
        columnHelper.accessor('legType', {
            header: 'Leg Type',
            cell: ({ getValue }) => (React.createElement("div", { className: "whitespace-nowrap" }, getLegTypeBadge(getValue()))),
            size: 130,
        }),
        columnHelper.accessor('equipmentId', {
            header: 'Equipment ID',
            cell: ({ row }) => {
                const shipment = row.original;
                const allEquipment = shipment.stops.flatMap((stop) => stop.equipmentIds);
                const uniqueEquipment = allEquipment.filter((eq, index, self) => index === self.findIndex((e) => e.id === eq.id));
                if (uniqueEquipment.length === 0) {
                    return React.createElement("span", { className: "text-muted-foreground" }, "-");
                }
                const isExpanded = (expandedEquipment === null || expandedEquipment === void 0 ? void 0 : expandedEquipment.has(shipment._id)) || false;
                const displayLimit = 2;
                const displayEquipment = isExpanded
                    ? uniqueEquipment
                    : uniqueEquipment.slice(0, displayLimit);
                const remainingCount = uniqueEquipment.length - displayLimit;
                return (React.createElement("div", { className: "flex flex-wrap gap-2 items-start" },
                    displayEquipment.map((equipment, index) => (React.createElement("div", { key: index, className: "flex items-center gap-2" },
                        React.createElement("button", { onClick: () => onEditEquipment(shipment._id, equipment.id, equipment.containerType), className: "text-blue-600 font-medium hover:text-blue-800 hover:underline cursor-pointer transition-colors" }, equipment.id),
                        React.createElement(Badge, { variant: "outline", className: "bg-blue-100 text-blue-800 border-blue-300" }, equipment.containerType)))),
                    !isExpanded && remainingCount > 0 && (React.createElement(Badge, { variant: "outline", className: "bg-gray-100 text-gray-600 border-gray-300 cursor-pointer hover:bg-gray-200 transition-colors", onClick: () => toggleEquipmentExpand === null || toggleEquipmentExpand === void 0 ? void 0 : toggleEquipmentExpand(shipment._id) },
                        "+",
                        remainingCount)),
                    isExpanded && uniqueEquipment.length > displayLimit && (React.createElement(Badge, { variant: "outline", className: "bg-gray-100 text-gray-600 border-gray-300 cursor-pointer hover:bg-gray-200 transition-colors", onClick: () => toggleEquipmentExpand === null || toggleEquipmentExpand === void 0 ? void 0 : toggleEquipmentExpand(shipment._id) }, "Show less"))));
            },
            size: 200,
        }),
        columnHelper.accessor('pickup', {
            header: 'Pickup',
            cell: ({ getValue }) => {
                const pickup = getValue();
                return (React.createElement("div", null,
                    pickup.name && (React.createElement("div", { className: "font-bold text-gray-800" }, pickup.name)),
                    React.createElement("div", { className: "text-muted-foreground" }, pickup.address)));
            },
            size: 250,
        }),
        columnHelper.accessor('deliver', {
            header: 'Deliver',
            cell: ({ getValue }) => {
                const deliver = getValue();
                return (React.createElement("div", null,
                    deliver.name && (React.createElement("div", { className: "font-bold text-gray-800" }, deliver.name)),
                    React.createElement("div", { className: "text-muted-foreground" }, deliver.address)));
            },
            size: 250,
        }),
        columnHelper.accessor('carrier', {
            header: 'Carrier',
            cell: ({ getValue }) => (React.createElement("span", { className: "text-blue-600" }, getValue())),
            size: 120,
        }),
        columnHelper.accessor('deliveryOrder', {
            header: 'Delivery Order',
            cell: ({ row }) => {
                return (React.createElement("div", { className: "flex items-start gap-2" },
                    React.createElement(Button, { variant: "outline", size: "sm", className: "h-7 px-2 cursor-pointer hover:bg-blue-50 hover:text-blue-600", onClick: () => { var _a; return (_a = options.onOpenDeliveryOrder) === null || _a === void 0 ? void 0 : _a.call(options, row.original._id); }, title: "Open latest Delivery Order" },
                        React.createElement(Download, { className: "h-3 w-3" }),
                        React.createElement("span", { className: "ml-2" }, "Open"))));
            },
            size: 180,
        }),
        columnHelper.display({
            id: 'actions',
            header: 'Actions',
            cell: ({ row }) => {
                if (!isMounted) {
                    return (React.createElement(Button, { variant: "ghost", size: "sm", className: "h-8 w-8 p-0", disabled: true },
                        React.createElement(MoreHorizontal, { className: "h-4 w-4" })));
                }
                const isClosedOrCancelled = ['job closed', 'job canceled'].includes(row.original.status.toLowerCase());
                return (React.createElement(DropdownMenu, null,
                    React.createElement(DropdownMenuTrigger, { asChild: true },
                        React.createElement(Button, { variant: "ghost", size: "sm", className: "h-8 w-8 p-0" },
                            React.createElement(MoreHorizontal, { className: "h-4 w-4" }))),
                    React.createElement(DropdownMenuContent, { align: "end" },
                        React.createElement(DropdownMenuItem, { disabled: isClosedOrCancelled, onClick: () => onEditShipment(row.original) },
                            React.createElement(Pencil, { className: "mr-2 h-4 w-4" }),
                            "Edit"),
                        React.createElement(DropdownMenuItem, { disabled: isClosedOrCancelled, onClick: () => onDeleteShipment(row.original), className: "text-red-600" },
                            React.createElement(X, { className: "mr-2 h-4 w-4" }),
                            "Cancel"),
                        React.createElement(DropdownMenuSeparator, null),
                        React.createElement(DropdownMenuItem, { asChild: true },
                            React.createElement(Link, { to: `/shipments/${row.original._id}` },
                                React.createElement(Eye, { className: "mr-2 h-4 w-4" }),
                                "View Details")),
                        React.createElement(DropdownMenuItem, { disabled: isClosedOrCancelled, onClick: () => onOpenAddHub(row.original) },
                            React.createElement(Plus, { className: "mr-2 h-4 w-4" }),
                            "Add Hub"),
                        React.createElement(DropdownMenuItem, { onClick: () => { var _a; return (_a = options.onGenerateDeliveryOrder) === null || _a === void 0 ? void 0 : _a.call(options, row.original._id); } },
                            React.createElement(FileText, { className: "mr-2 h-4 w-4" }),
                            "Generate Delivery Order"),
                        React.createElement(DropdownMenuItem, { onClick: () => { var _a; return (_a = options.onSendDeliveryOrder) === null || _a === void 0 ? void 0 : _a.call(options, row.original._id); } },
                            React.createElement(Mail, { className: "mr-2 h-4 w-4" }),
                            "Email Delivery Order"),
                        React.createElement(DropdownMenuItem, { onClick: () => { var _a; return (_a = options.onOpenDeliveryOrder) === null || _a === void 0 ? void 0 : _a.call(options, row.original._id); } },
                            React.createElement(Download, { className: "mr-2 h-4 w-4" }),
                            "Open Delivery Order"))));
            },
            size: 80,
        }),
    ];
}
